import React from 'react';
import {Box, Grid, Typography} from '@mui/material';
import apiService from '../../services/apiService';
import {ROLES} from '../../config/roles';
import EditableCard from '../EditableCard';
import MachineCameraForm from '../forms/MachineCameraForm';
import WhepVideoPlayer from '../WhepVideoPlayer';

/**
 * "Camera" card on the machine detail page. View = the WHEP URL + live
 * preview for each camera. Edit = TextField per URL with live preview
 * that follows what you type, persisted via PUT /api/machines/{id}/camera.
 *
 * Refactored from the original standalone implementation (which had its
 * own toggle + save logic) onto the shared {@link EditableCard} +
 * {@link MachineCameraForm} pattern. Net result: a single source of truth
 * for "inline view/edit card" behaviour, identical UX across every card.
 */
const MachineCameraCard = ({machine, onUpdated}) => (
    <EditableCard
        title="Camera"
        protectedBy={MachineCameraCard}
        protectAction="edit"
        value={{
            primaryUrl: machine.cameraPrimaryUrl ?? '',
            secondaryUrl: machine.cameraSecondaryUrl ?? '',
        }}
        save={(draft) => apiService.put(`/api/machines/${machine.id}/camera`, {
            primaryUrl: blankToNull(draft.primaryUrl),
            secondaryUrl: blankToNull(draft.secondaryUrl),
        })}
        onSaved={onUpdated}
        successMessage="Camera URLs saved"
        renderView={() => <CameraView machine={machine}/>}
        renderForm={(draft, setDraft) => (
            <MachineCameraForm value={draft} onChange={setDraft}/>
        )}
    />
);

const CameraView = ({machine}) => (
    <Grid container spacing={2}>
        <CameraColumn label="Primary" url={machine.cameraPrimaryUrl} previewLabel="main"/>
        <CameraColumn label="Secondary" url={machine.cameraSecondaryUrl} previewLabel="second"/>
    </Grid>
);

const CameraColumn = ({label, url, previewLabel}) => (
    <Grid size={{xs: 12, md: 6}}>
        <Typography variant="subtitle2" gutterBottom>{label}</Typography>
        <UrlText url={url}/>
        <Box sx={{mt: 1}}>
            <WhepVideoPlayer url={url} label={previewLabel}/>
        </Box>
    </Grid>
);

const UrlText = ({url}) => (
    <Typography variant="body2"
                sx={{
                    wordBreak: 'break-all',
                    color: url ? 'text.primary' : 'text.disabled',
                    fontFamily: 'monospace',
                }}>
        {url || '— not set —'}
    </Typography>
);

const blankToNull = (s) => (s == null || s.trim() === '' ? null : s.trim());

MachineCameraCard.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    edit: [ROLES.ADMINS],
};

export default MachineCameraCard;
